import { DashboardAnalysisSubtitle } from '@modules/dashboard/components/DashboardAnalysisHeader/DashboardAnalysisSubtitle';
import {
  getAnalysisTypeDisplayName,
  trackDashboardEvent,
} from '@modules/dashboard/helpers';
import {
  useGetDashboardAnalysisLinkWithState,
  useGetDashboardLinkWithGlobalFilterParams,
} from '@modules/dashboard/hooks';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { Box, Stack, Tooltip, alpha } from '@mui/material';
import { AnalysisViewFragment, Status } from 'generated/graphql';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardAnalysisHeaderActions } from './DashboardAnalysisHeaderActions';
import { DashboardAnalysisTitle } from './DashboardAnalysisTitle';

interface DashboardAnalysisHeaderProps {
  rowID: string;
  analysis: AnalysisViewFragment;
  shouldDisableInteraction?: boolean;
}

export const DashboardAnalysisHeader = ({
  rowID,
  analysis,
  shouldDisableInteraction: shouldDisableInteractionProp = false,
}: DashboardAnalysisHeaderProps) => {
  const navigate = useNavigate();

  const { analysisLink, analysisLinkState } =
    useGetDashboardAnalysisLinkWithState(analysis);

  const isDefaultContext = useDashboardStore(
    ({ visual }) => visual.context === 'Default',
  );

  const dashboardLinkWithGlobalFilters =
    useGetDashboardLinkWithGlobalFilterParams();

  const openAnalysis = () => {
    if (isDefaultContext) {
      window.history.replaceState(null, '', dashboardLinkWithGlobalFilters);
    }

    navigate(analysisLink, { state: analysisLinkState });

    trackDashboardEvent({
      eventName: `Analysis Opened from Dashboard`,
      dashboardLink: window.location.href,
      analysisLink,
    });
  };

  const isAnalysisArchived = analysis.status === Status.Archived;

  const shouldDisableInteraction =
    shouldDisableInteractionProp || isAnalysisArchived;

  const [showHeaderTooltip, setShowHeaderTooltip] = useState<boolean>(false);

  const handleTooltip = (show: boolean) => {
    setShowHeaderTooltip(show);
  };

  return (
    <Tooltip
      open={showHeaderTooltip}
      placement="top"
      title={`Click to open in ${getAnalysisTypeDisplayName(analysis.useCase)}`}
      arrow
      onMouseEnter={() => setShowHeaderTooltip(true)}
      onMouseLeave={() => setShowHeaderTooltip(false)}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        pl={2}
        py={1}
        spacing={1}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          '&:hover': {
            backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.08),
          },
        }}
      >
        <Stack
          direction="column"
          sx={{
            cursor: shouldDisableInteraction ? undefined : 'pointer',
            width: '100%',
          }}
          onClick={shouldDisableInteraction ? undefined : openAnalysis}
          className="dashboard-analysis-header"
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box width="calc(100% - 70px)">
              <DashboardAnalysisTitle analysis={analysis} />
            </Box>
            {!shouldDisableInteraction && (
              <Box
                onClick={(event) => event.stopPropagation()}
                onMouseEnter={() => handleTooltip(false)}
                onMouseLeave={() => handleTooltip(true)}
              >
                <DashboardAnalysisHeaderActions
                  rowID={rowID}
                  analysis={analysis}
                  analysisLink={analysisLink}
                />
              </Box>
            )}
          </Stack>
          <DashboardAnalysisSubtitle analysis={analysis} />
        </Stack>
      </Stack>
    </Tooltip>
  );
};
